// src/hooks/useClabSelectionRequests.js
import { useState, useEffect, useCallback, useMemo } from "react";
import { clabApplicationAPI, rejectionReasonAPI, clabAPI } from "../utils/api";
import {
  CLAB_SELECTION_TABS,
  getTabByKey,
} from "../constants/clabSelectionTabs";

const extractApplications = (payload = {}) => {
  const candidates = [
    payload?.data,
    payload?.data?.data,
    payload?.data?.data?.data,
    payload?.items,
    payload?.results,
    payload?.applications,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
};

const extractPagination = (payload = {}) => {
  const candidates = [
    payload?.pagination,
    payload?.meta,
    payload?.data?.pagination,
    payload?.data?.meta,
  ];

  return candidates.find(Boolean) || null;
};

/**
 * Custom hook for managing CLAB selection requests data and operations
 * @param {string} activeTab - Current active tab
 * @param {number} currentPage - Current page number
 * @param {number} itemsPerPage - Items per page
 * @returns {Object} Hook state and methods
 */
export const useClabSelectionRequests = (
  activeTab,
  currentPage,
  itemsPerPage,
  search = "",
  searchBy = "all"
) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [rejectionReasonsLoading, setRejectionReasonsLoading] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});

  // Memoized API parameters to prevent unnecessary re-renders
  const apiParams = useMemo(
    () => ({
      page: currentPage,
      per_page: itemsPerPage,
      status: activeTab,
      ...(search ? { search, search_by: searchBy } : {}),
    }),
    [currentPage, itemsPerPage, activeTab, search, searchBy]
  );

  // Load applications with error handling and loading states
  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let result;
      if (activeTab === "pending") {
        result = await clabApplicationAPI.getPendingApplications(apiParams);
      } else {
        result = await clabApplicationAPI.getAllApplications(apiParams);
      }

      if (result?.status === "error") {
        throw new Error(result?.message || "Failed to load applications");
      }

      const payload = result?.data || {};
      setApplications(extractApplications(payload));
      setPagination(extractPagination(payload));
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to load applications";
      setError(errorMessage);
      console.error("Error loading applications:", error);

      // Reset applications on error to prevent stale data
      setApplications([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [activeTab, apiParams]);

  const loadStatusCounts = useCallback(async () => {
    try {
      const counts = await clabAPI.getSelectionRequestCounts();
      setStatusCounts(counts || {});
    } catch (error) {
      console.error("Failed to load selection request counts:", error);
      setStatusCounts({});
    }
  }, []);

  // Load rejection reasons with separate loading state
  const loadRejectionReasons = useCallback(async () => {
    try {
      setRejectionReasonsLoading(true);
      const result = await rejectionReasonAPI.getActiveReasons();

      let reasons = [];
      console.warn("Raw API response:", result);
      if (Array.isArray(result)) {
        reasons = result;
      } else if (Array.isArray(result?.data?.data?.data)) {
        reasons = result.data.data.data;
      } else if (Array.isArray(result?.data?.data)) {
        reasons = result.data.data;
      } else if (Array.isArray(result?.data)) {
        reasons = result.data;
      }

      setRejectionReasons(reasons);
    } catch (error) {
      console.error("Error loading rejection reasons:", error);
      setRejectionReasons([]);
    } finally {
      setRejectionReasonsLoading(false);
    }
  }, []);

  // Refresh data with optimistic updates
  const refreshData = useCallback(async () => {
    await Promise.all([loadApplications(), loadRejectionReasons(), loadStatusCounts()]);
  }, [loadApplications, loadRejectionReasons, loadStatusCounts]);

  // Load applications when dependencies change
  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  // Load rejection reasons once on mount
  useEffect(() => {
    loadRejectionReasons();
  }, [loadRejectionReasons]);

  useEffect(() => {
    loadStatusCounts();
  }, [loadStatusCounts]);

  return {
    // Data
    applications,
    rejectionReasons,
    statusCounts,
    pagination,

    // Loading states
    loading,
    rejectionReasonsLoading,

    // Error state
    error,

    // Methods
    loadApplications,
    loadRejectionReasons,
    refreshData,

    // Utilities
    setError,
  };
};

export default useClabSelectionRequests;
